import { ReceivableAvailabilitySchema } from '@shared/contracts/receivable-availability'
import { z } from 'zod'
import {
  DebtOriginSchema,
  DebtStatusSchema,
  PaymentMethodSchema,
  SaleStatusSchema,
  SubscriptionStatusSchema,
} from '@shared/contracts/enums'
import type { SaleStatus, Currency } from '@shared/contracts/enums'
import { MonetaryValueSchema } from '@shared/lib/money/money'

export const SaleFinancialEventKindSchema = z.enum([
  'PARTIAL_REFUND',
  'REFUND',
  'PREVENTED_CHARGEBACK',
  'CHARGEBACK',
  'DISPUTE_WON',
  'FX_LOSS',
])

export const SaleFinancialEventStateSchema = z.enum(['RECOVERING', 'RECOVERED', 'REVERSED'])

export const SaleFinancialEventSchema = z.object({
  kind: SaleFinancialEventKindSchema,
  fx: z
    .object({
      referenceId: z.string(),
      originalBasis: MonetaryValueSchema,
      providerDebit: MonetaryValueSchema,
      loss: MonetaryValueSchema,
      originalTransactionId: z.string(),
      transactionIds: z.array(z.string()),
    })
    .optional(),
  state: SaleFinancialEventStateSchema,
  chargeAffectedAmount: MonetaryValueSchema.optional(),
  affectedAmount: MonetaryValueSchema,
  recoveredAmount: MonetaryValueSchema,
  recoveryVerified: z.boolean().optional(),
  returnedAmount: MonetaryValueSchema.optional(),
  canceledAmount: MonetaryValueSchema.optional(),
  feeReturnedAmount: MonetaryValueSchema.optional(),
  feeCanceledAmount: MonetaryValueSchema.optional(),
  outstandingAmount: MonetaryValueSchema,
  occurredAt: z.string(),
  feeAmount: MonetaryValueSchema.optional(),
  feeRecoveredAmount: MonetaryValueSchema.optional(),
  feeOutstandingAmount: MonetaryValueSchema.optional(),
})

export const SaleCouponSettlementSchema = z.object({
  amountBeforeDiscount: MonetaryValueSchema,
  discountAmount: MonetaryValueSchema,
})

export const SaleCouponSchema = z.object({
  code: z.string(),
  type: z.enum(['percent', 'fixed']),
  percent: z.number().optional(),
  configuredAmount: MonetaryValueSchema.optional(),
  amountBeforeDiscount: MonetaryValueSchema,
  discountAmount: MonetaryValueSchema,
  settlement: SaleCouponSettlementSchema.optional(),
})

export const SaleListItemSchema = z.object({
  id: z.string(),
  buyerName: z.string(),
  productName: z.string(),
  amount: MonetaryValueSchema,
  grossAmount: MonetaryValueSchema.optional(),
  commercialAmount: MonetaryValueSchema.optional(),
  fxVariation: MonetaryValueSchema.optional(),
  participationGrossAmount: MonetaryValueSchema.optional(),
  status: SaleStatusSchema,
  createdAt: z.string(),
  affiliateSale: z.boolean().optional(),
  globalMarket: z.boolean().optional(),
  funnelItemCount: z.number().int().nonnegative().optional(),
  originSaleId: z.string().optional(),
  subscriptionId: z.string().optional(),
  subscriptionCycleN: z.number().int().positive().optional(),
  subscriptionStatus: SubscriptionStatusSchema.optional(),
  isRenewal: z.boolean().optional(),
  subscriptionChargeCount: z.number().int().nonnegative().optional(),
  subscriptionRenewalCount: z.number().int().nonnegative().optional(),
  subscriptionAccumulated: MonetaryValueSchema.optional(),
  subscriptionLastActivityAt: z.string().optional(),
  subscriptionLastChargeStatus: SaleStatusSchema.optional(),
  handledByGlobalChargeback: z.boolean().optional(),
  financialEvents: z.array(SaleFinancialEventSchema).optional(),
  coupon: SaleCouponSchema.optional(),
})

export const SaleSplitRoleSchema = z.enum(['PRODUCER', 'COPRODUCER', 'AFFILIATE', 'SEED'])

export const SaleSplitPartSchema = z.object({
  role: SaleSplitRoleSchema,
  name: z.string(),
  isYou: z.boolean(),
  percentage: z.string().optional(),
  amount: MonetaryValueSchema,
  available: MonetaryValueSchema.optional(),
  reserve: MonetaryValueSchema.optional(),
})

export const SaleLineSchema = z.object({
  lineId: z.string().optional(),
  kind: z.string(),
  productName: z.string(),
  offerName: z.string().optional(),
  amount: MonetaryValueSchema,
  platformFee: MonetaryValueSchema.optional(),
  recuperaFee: MonetaryValueSchema.optional(),
  currencyConversionFee: MonetaryValueSchema.optional(),
  fixedFee: MonetaryValueSchema.optional(),
  reserveAmount: MonetaryValueSchema.optional(),
  netAmount: MonetaryValueSchema.optional(),
  youCommissioned: z.boolean().optional(),
  youAmount: MonetaryValueSchema.optional(),
})

export const SaleRefundSchema = z.object({
  origin: DebtOriginSchema,
  status: DebtStatusSchema,
  refundedAmount: MonetaryValueSchema,
  recoveredAmount: MonetaryValueSchema,
  outstandingAmount: MonetaryValueSchema,
  createdAt: z.string(),
})

export const SaleDebtApplicationSchema = z.object({
  hasDebtConsumption: z.boolean(),
  consumedReceivableAmount: MonetaryValueSchema,
  consumedReserveAmount: MonetaryValueSchema,
  remainingReceivableAmount: MonetaryValueSchema,
  remainingReserveAmount: MonetaryValueSchema,
})

export const SaleFunnelStageSchema = z.object({
  commercialAmount: MonetaryValueSchema.optional(),
  fxVariation: MonetaryValueSchema.optional(),
  saleId: z.string(),
  createdAt: z.string().optional(),
  kind: z.string(),
  productName: z.string(),
  offerName: z.string().optional(),
  status: SaleStatusSchema,
  handledByGlobalChargeback: z.boolean().optional(),
  amount: MonetaryValueSchema,
  grossAmount: MonetaryValueSchema,
  platformFee: MonetaryValueSchema,
  recuperaFee: MonetaryValueSchema.optional(),
  fixedFee: MonetaryValueSchema.optional(),
  currencyConversionFee: MonetaryValueSchema.optional(),
  reserveAmount: MonetaryValueSchema,
  netAmount: MonetaryValueSchema,
  debtApplication: SaleDebtApplicationSchema.optional(),
  availableAt: z.string().optional(),
  availability: ReceivableAvailabilitySchema.optional(),
  youCommissioned: z.boolean().optional(),
  youAmount: MonetaryValueSchema.optional(),
  split: z.array(SaleSplitPartSchema),
  subscriptionId: z.string().optional(),
  subscriptionCycleN: z.number().int().positive().optional(),
  subscriptionStatus: SubscriptionStatusSchema.optional(),
  isRenewal: z.boolean().optional(),
  financialEvents: z.array(SaleFinancialEventSchema).optional(),
  permissions: z
    .object({
      canRequestRefund: z.boolean().optional().default(false),
    })
    .optional(),
})

export const SaleDetailSchema = SaleListItemSchema.extend({
  buyerEmail: z.string().optional(),
  offerName: z.string().optional(),
  paymentMethod: PaymentMethodSchema.optional(),
  installments: z.number().optional(),
  receivingPeriod: z.string().optional(),
  receivingPlan: z
    .object({
      kind: z.enum(['STANDARD', 'CUSTOM']),
      receivingDays: z.number().int().min(0).max(3650),
    })
    .optional(),
  exchangeRate: z.string().optional(),
  buyerPaidConversion: z.boolean().optional(),
  serviceFeeQuote: z
    .object({
      policy: z.enum(['UNIVERSAL_USD_FIXED_V1', 'CUSTOM_USD_FIXED_V1']),
      percentage: z.string(),
      sourceAmount: z.string(),
      sourceCurrency: z.literal('USD'),
      fixedAmount: z.string(),
      currency: z.string(),
      rate: z.string(),
      quotedAt: z.string(),
      expiresAt: z.string(),
      providerQuoteId: z.string().optional(),
      rateKind: z.string(),
    })
    .optional(),
  grossAmount: MonetaryValueSchema,
  platformFee: MonetaryValueSchema,
  recuperaFee: MonetaryValueSchema.optional(),
  fixedFee: MonetaryValueSchema.optional(),
  currencyConversionFee: MonetaryValueSchema.optional(),
  reserveAmount: MonetaryValueSchema,
  netAmount: MonetaryValueSchema,
  debtApplication: SaleDebtApplicationSchema.optional(),
  availableAt: z.string().optional(),
  availability: ReceivableAvailabilitySchema.optional(),
  split: z.array(SaleSplitPartSchema),
  lines: z.array(SaleLineSchema).optional(),
  funnelStages: z.array(SaleFunnelStageSchema).optional(),
  refund: SaleRefundSchema.optional(),
  financialEvents: z.array(SaleFinancialEventSchema).optional(),
  permissions: z
    .object({
      canRequestRefund: z.boolean().optional().default(false),
    })
    .optional(),
})

export const SaleListSchema = z.array(SaleListItemSchema)

export const SalePageSchema = z.object({
  items: z.array(SaleListItemSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export interface SalesQueryParams {
  cursor?: string
  limit?: number
  status?: SaleStatus[]
  productId?: string
  currency?: Currency
  dateFrom?: string
  dateTo?: string
  q?: string
}

export type SalePage = z.infer<typeof SalePageSchema>

export type SaleListItem = z.infer<typeof SaleListItemSchema>
export type SaleCouponSettlement = z.infer<typeof SaleCouponSettlementSchema>
export type SaleCoupon = z.infer<typeof SaleCouponSchema>
export type SaleFinancialEventKind = z.infer<typeof SaleFinancialEventKindSchema>
export type SaleFinancialEventState = z.infer<typeof SaleFinancialEventStateSchema>
export type SaleFinancialEvent = z.infer<typeof SaleFinancialEventSchema>
export type SaleSplitRole = z.infer<typeof SaleSplitRoleSchema>
export type SaleSplitPart = z.infer<typeof SaleSplitPartSchema>
export type SaleLine = z.infer<typeof SaleLineSchema>
export type SaleDebtApplication = z.infer<typeof SaleDebtApplicationSchema>
export type SaleFunnelStage = z.infer<typeof SaleFunnelStageSchema>
export type SaleRefund = z.infer<typeof SaleRefundSchema>
export type SaleDetail = z.infer<typeof SaleDetailSchema>
